import React, { useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, X, Loader2, CalendarPlus } from "lucide-react";
import { fmtDate } from "@/lib/format";

// Ma=1 ... Zo=0 (JS getDay)
const WEEKDAYS = [
  { key: 1, label: "Ma" }, { key: 2, label: "Di" }, { key: 3, label: "Wo" },
  { key: 4, label: "Do" }, { key: 5, label: "Vr" }, { key: 6, label: "Za" }, { key: 0, label: "Zo" },
];

function isoWeek(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayNum + 3);
  const firstThursday = date.getTime();
  date.setUTCMonth(0, 1);
  if (date.getUTCDay() !== 4) {
    date.setUTCMonth(0, 1 + ((4 - date.getUTCDay()) + 7) % 7);
  }
  return 1 + Math.round((firstThursday - date.getTime()) / 604800000);
}

function toYMD(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function BulkServiceDialog({ open, onOpenChange, types, onDone }) {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [days, setDays] = useState(new Set());
  const [freq, setFreq] = useState("weekly");
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggleDay = (k) =>
    setDays((s) => {
      const n = new Set(s);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });

  const addSlot = () => setSlots((s) => [...s, { service_type_id: "", desired: 1 }]);
  const setSlot = (i, k, v) => setSlots((s) => { const n = [...s]; n[i] = { ...n[i], [k]: v }; return n; });
  const removeSlot = (i) => setSlots((s) => s.filter((_, x) => x !== i));

  const dates = useMemo(() => {
    if (!from || !to || days.size === 0) return [];
    const out = [];
    const start = new Date(from + "T00:00:00");
    const end = new Date(to + "T00:00:00");
    if (end < start) return [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      if (!days.has(d.getDay())) continue;
      if (freq === "even" && isoWeek(d) % 2 !== 0) continue;
      if (freq === "odd" && isoWeek(d) % 2 !== 1) continue;
      out.push(toYMD(d));
      if (out.length > 500) break;
    }
    return out;
  }, [from, to, days, freq]);

  const reset = () => {
    setName(""); setLocation(""); setFrom(""); setTo(""); setStartTime(""); setEndTime("");
    setDays(new Set()); setFreq("weekly"); setSlots([]);
  };

  const submit = async () => {
    if (!name || !startTime || !endTime || dates.length === 0) {
      return toast.error("Vul naam, tijden, periode en minstens één weekdag in");
    }
    setBusy(true);
    try {
      const slotPayload = slots.filter((s) => s.service_type_id).map((s) => {
        const t = types.find((x) => x.id === s.service_type_id);
        return { service_type_id: s.service_type_id, service_type_name: t?.name || "",
                 desired: parseInt(s.desired, 10) || 1, min: 0, max: 0 };
      });
      const services = dates.map((date) => ({
        date, start_time: startTime, end_time: endTime,
        name, location: location || null, notes: null, slots: slotPayload,
      }));
      const { data } = await api.post("/services/bulk", { services });
      toast.success(`${data.created} diensten toegevoegd`);
      reset();
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarPlus className="w-5 h-5" /> Diensten in bulk toevoegen</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <div className="space-y-1.5"><Label>Naam</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="bv. Poetsen of Bar-avond" data-testid="bulk-name" /></div>

          <div>
            <Label>Weekdagen</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {WEEKDAYS.map((w) => (
                <button key={w.key} type="button" onClick={() => toggleDay(w.key)} data-testid={`bulk-day-${w.key}`}
                  className={`w-11 h-10 rounded-lg border-2 text-sm font-semibold transition-colors ${
                    days.has(w.key) ? "bg-red-600 text-white border-red-600" : "border-slate-200 dark:border-slate-700 text-muted-foreground hover:border-red-400"
                  }`}>{w.label}</button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Frequentie</Label>
            <Select value={freq} onValueChange={setFreq}>
              <SelectTrigger data-testid="bulk-freq"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Elke week</SelectItem>
                <SelectItem value="even">Om de week — even weken</SelectItem>
                <SelectItem value="odd">Om de week — oneven weken</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Tip: voor "ene week dinsdag, andere week woensdag" voeg je twee keer toe: dinsdag (even) en woensdag (oneven).</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Van</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="bulk-from" /></div>
            <div className="space-y-1.5"><Label>Tot en met</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="bulk-to" /></div>
            <div className="space-y-1.5"><Label>Begintijd</Label><Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="bulk-start" /></div>
            <div className="space-y-1.5"><Label>Eindtijd</Label><Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} data-testid="bulk-end" /></div>
          </div>
          <div className="space-y-1.5"><Label>Locatie</Label><Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="'t Kelderke" data-testid="bulk-location" /></div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Functies (optioneel)</Label>
              <Button variant="outline" size="sm" onClick={addSlot} data-testid="bulk-add-slot"><Plus className="w-3.5 h-3.5 mr-1" /> Functie</Button>
            </div>
            <div className="space-y-2">
              {slots.map((sl, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Select value={sl.service_type_id} onValueChange={(v) => setSlot(i, "service_type_id", v)}>
                    <SelectTrigger className="flex-1" data-testid={`bulk-slot-type-${i}`}><SelectValue placeholder="Werkzaamheid" /></SelectTrigger>
                    <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" className="w-16" value={sl.desired} onChange={(e) => setSlot(i, "desired", e.target.value)} data-testid={`bulk-slot-desired-${i}`} />
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-rose-600" onClick={() => removeSlot(i)}><X className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border bg-muted/40 p-3" data-testid="bulk-preview">
            <p className="text-sm font-semibold mb-1">{dates.length} diensten worden aangemaakt</p>
            {dates.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {dates.slice(0, 8).map((d) => fmtDate(d)).join(" · ")}{dates.length > 8 ? ` … +${dates.length - 8}` : ""}
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button onClick={submit} disabled={busy || dates.length === 0} data-testid="bulk-submit">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {dates.length} diensten toevoegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
